// Iteration 088 -- Coach notes section (rendered on player-edit for coaches
// at the player's primary school). The API returns an empty list for users
// who aren't coaches of that school, so this renders nothing for them.
import { useCallback, useEffect, useState } from "react";
import { NOTE_TAGS, type NoteTag, type PlayerCoachNotePublic } from "@dugads/shared";
import * as api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Loader2, Plus, Trash2 } from "lucide-react";

// 142 -- inverted-contrast tag chips (WCAG AA).
const TAG_STYLE: Record<NoteTag, string> = {
  performance: "bg-[#E6F1FB] text-[#0C447C]",
  injury: "bg-[#FCEBEB] text-[#791F1F]",
  discipline: "bg-[#FAEEDA] text-[#633806]",
  attendance: "bg-[#E1F5EE] text-[#085041]",
};

interface CoachNotesSectionProps {
  playerId: string;
}

export function CoachNotesSection({ playerId }: CoachNotesSectionProps) {
  const [notes, setNotes] = useState<PlayerCoachNotePublic[]>([]);
  const [loading, setLoading] = useState(true);
  const [canAuthor, setCanAuthor] = useState(false);
  const [draft, setDraft] = useState("");
  const [draftTag, setDraftTag] = useState<NoteTag | "">("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingBody, setEditingBody] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.getCoachNotes(playerId);
      setNotes(res.notes);
      // Probe authoring: attempt a small visibility check by trying to create a
      // dummy note is too invasive; instead use the presence of any note, OR
      // attempt the POST when the user opts to and surface the 403 inline.
      setCanAuthor(true);
    } catch {
      setNotes([]);
      setCanAuthor(false);
    } finally {
      setLoading(false);
    }
  }, [playerId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function create() {
    if (!draft.trim()) return;
    setSaving(true);
    setError(null);
    try {
      await api.createCoachNote(playerId, { body: draft.trim(), tag: draftTag || null });
      setDraft("");
      setDraftTag("");
      await load();
    } catch (e) {
      // 403 here means the current user isn't a coach at this player's school
      if (e instanceof api.ApiError && e.status === 403) {
        setCanAuthor(false);
        setError(null);
      } else {
        setError(e instanceof Error ? e.message : "Failed to save note");
      }
    } finally {
      setSaving(false);
    }
  }

  async function saveEdit(noteId: string) {
    if (!editingBody.trim()) return;
    setSaving(true);
    setError(null);
    try {
      await api.updateCoachNote(playerId, noteId, { body: editingBody.trim() });
      setEditingId(null);
      setEditingBody("");
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to update note");
    } finally {
      setSaving(false);
    }
  }

  async function remove(noteId: string) {
    if (!confirm("Delete this note?")) return;
    setSaving(true);
    try {
      await api.deleteCoachNote(playerId, noteId);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to delete note");
    } finally {
      setSaving(false);
    }
  }

  if (loading) return null;

  // If the user is verifiably not a coach (403 on create attempt above), hide
  // the whole section rather than show an empty, non-interactive panel.
  if (!canAuthor && notes.length === 0) return null;

  return (
    <Card data-region="coach-notes">
      <CardHeader>
        <CardTitle className="text-base">Player Development Log</CardTitle>
        <p className="text-xs text-muted-foreground">
          Visible to this player's coaches and school admins only -- never to
          parents or the player. Tag a note, or mark it private to keep it to
          yourself.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        {notes.length === 0 && (
          <p className="text-sm text-muted-foreground">No notes yet.</p>
        )}
        <ul className="space-y-2">
          {notes.map((n) => (
            <li key={n.id} className="rounded border bg-muted/10 p-2">
              {editingId === n.id ? (
                <div className="space-y-2">
                  <textarea
                    className="w-full rounded border px-3 py-2 text-sm"
                    rows={3}
                    value={editingBody}
                    onChange={(e) => setEditingBody(e.target.value)}
                  />
                  <div className="flex gap-2">
                    <Button size="sm" onClick={() => saveEdit(n.id)} disabled={saving}>
                      Save
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setEditingId(null);
                        setEditingBody("");
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1">
                    <div className="mb-1 flex flex-wrap items-center gap-1.5">
                      {n.tag && (
                        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium capitalize ${TAG_STYLE[n.tag]}`}>
                          {n.tag}
                        </span>
                      )}
                      {n.visibility === "private" && (
                        <span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium text-muted-foreground">
                          private
                        </span>
                      )}
                    </div>
                    <p className="whitespace-pre-wrap text-sm">{n.body}</p>
                    <p className="mt-1 text-[10px] text-muted-foreground">
                      {n.coach_name} &middot; {new Date(n.created_at).toLocaleString()}
                    </p>
                  </div>
                  <div className="flex gap-1">
                    <button
                      type="button"
                      className="text-xs text-muted-foreground hover:underline"
                      onClick={() => {
                        setEditingId(n.id);
                        setEditingBody(n.body);
                      }}
                    >
                      Edit
                    </button>
                    <Button variant="ghost" size="sm" onClick={() => remove(n.id)}>
                      <Trash2 className="h-3 w-3 text-destructive" />
                    </Button>
                  </div>
                </div>
              )}
            </li>
          ))}
        </ul>

        {canAuthor && (
          <div className="space-y-2 border-t pt-3">
            <textarea
              className="w-full rounded border px-3 py-2 text-sm"
              rows={2}
              placeholder="New note…"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
            />
            <select
              aria-label="Note tag"
              className="rounded border bg-background px-2 py-1 text-sm"
              value={draftTag}
              onChange={(e) => setDraftTag(e.target.value as NoteTag | "")}
            >
              <option value="">No tag</option>
              {NOTE_TAGS.map((t) => (
                <option key={t} value={t} className="capitalize">{t}</option>
              ))}
            </select>
            {error && <p className="text-xs text-destructive" data-error>{error}</p>}
            <Button size="sm" onClick={create} disabled={saving || !draft.trim()}>
              {saving ? (
                <>
                  <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                  Saving…
                </>
              ) : (
                <>
                  <Plus className="mr-1 h-3 w-3" />
                  Add note
                </>
              )}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
